import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTheme } from '@nfinite/react-hubstairs/styled/theme'
import {
  ActiveBadge,
  ArrowIcon,
  Column,
  Container,
  ContractLabel,
  Name,
  Organization,
  PlaceholderBar,
  PlaceholderCircle,
  PlaceholderContainer,
  PlaceholderTag,
  SwitchButton,
  TagNoCapitalize,
} from './styled'
import fallbackOrganization from 'assets/svg/fallback_organization.svg'
import ArrowLeftSvg from 'assets/svg/arrow_left.svg?react'
import { CONTRACT_STATUS, CONTRACT_TYPE_SOFTWARE } from 'utils/contracts'
import { FormattedMessage } from 'components/Commons/Translation'
import { useProfile } from 'context/Profile'
import translations from './translations'
import Tag from 'components/Commons/Tag'
import { getTheme } from 'theme'
import IconLabel from 'components/Commons/Icon/IconLabel'
import OrganizationDemoBadge from 'components/Commons/OrganizationDemoBadge'

const TAG_COLORS = getTheme().colors.tag

const TRANSITION = { duration: 0.3, ease: 'easeInOut' }

function getAnimationVariants(theme: { getColor: (color: string, variant?: string) => string }) {
  const containerVariants = {
    normal: {
      backgroundColor: theme.getColor('gray', 'lighter'),
      boxShadow: '0px 0px 0px 0px rgba(0,0,0,0)',
      scale: 1,
    },
    hovered: {
      backgroundColor: theme.getColor('white'),
      boxShadow: '0px 4px 20px 0px #00000040',
      transition: TRANSITION,
      scale: 1.01,
    },
  }

  const textVariants = {
    normal: {
      y: 0,
      transition: TRANSITION,
    },
    hovered: {
      y: -25,
      transition: TRANSITION,
    },
  }

  const userRoleVariants = {
    normal: {
      y: 0,
      transition: TRANSITION,
    },
    hovered: {
      y: -45,
      transition: TRANSITION,
    },
  }

  const pictureVariants = {
    normal: {
      scale: 1,
      transition: TRANSITION,
    },
    hovered: {
      scale: 0.75,
      transition: TRANSITION,
    },
  }

  const buttonVariants = {
    hidden: {
      y: 80,
      transition: TRANSITION,
    },
    visible: {
      y: 0,
      transition: TRANSITION,
    },
  }

  return {
    containerVariants,
    textVariants,
    userRoleVariants,
    pictureVariants,
    buttonVariants,
  }
}

interface UserRoleProps {
  isGlobalRole: boolean
  role?: { id: string; currentLabel: string } | null
}

function UserRole({ isGlobalRole, role }: UserRoleProps) {
  if (isGlobalRole) {
    return (
      <TagNoCapitalize rawColor={TAG_COLORS.gray}>
        <FormattedMessage {...translations.crossOrgUser} />
      </TagNoCapitalize>
    )
  }

  if (!role) {
    return null
  }

  return <Tag.Auto stableString={role.id}>{role.currentLabel}</Tag.Auto>
}

interface OrganizationCardProps {
  isActive: boolean
  organization: {
    picture: string
    name: string
    forInternalTesting: boolean
    contracts: { contractType: string; currentLabel: string; status: string }[]
  }
  onClick: () => void
  role?: { id: string; currentLabel: string } | null
}
function OrganizationCard({ isActive, organization, onClick, role }: OrganizationCardProps) {
  const { picture, name, contracts = [], forInternalTesting } = organization
  const { isGlobalRole } = useProfile()
  const [isHovered, setIsHovered] = useState(false)
  const theme = useTheme()

  const { currentLabel, contractType } =
    contracts.find(
      ({ contractType, status }) =>
        Object.values(CONTRACT_TYPE_SOFTWARE).includes(contractType) && status === CONTRACT_STATUS.ENABLED,
    ) || {}

  const contractLabel = contractType === CONTRACT_TYPE_SOFTWARE.NFINITE ? '-' : currentLabel

  const { containerVariants, textVariants, userRoleVariants, pictureVariants, buttonVariants } =
    getAnimationVariants(theme)

  return (
    <Container
      data-testid="switch-organization-card"
      as={motion.div}
      isActive={isActive}
      onClick={onClick}
      initial="normal"
      animate={isHovered ? 'hovered' : 'normal'}
      variants={containerVariants}
      onMouseEnter={() => !isActive && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isActive && (
        <ActiveBadge>
          <FormattedMessage {...translations.active} />
        </ActiveBadge>
      )}
      <Column>
        <Organization as={motion.div} variants={pictureVariants} style={{ transformOrigin: 'top center' }}>
          <OrganizationDemoBadge forInternalTesting={forInternalTesting} className="organization-demo-badge" />
          <Organization.Picture picture={picture} fallback={fallbackOrganization} alt={name} round border />
        </Organization>
        <motion.div variants={textVariants} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Name>{organization.name}</Name>
          <ContractLabel>{contractLabel}</ContractLabel>
        </motion.div>
      </Column>
      <motion.div variants={userRoleVariants}>
        <UserRole isGlobalRole={isGlobalRole} role={role} />
      </motion.div>
      <motion.div
        initial="hidden"
        animate={isHovered ? 'visible' : 'hidden'}
        variants={buttonVariants}
        style={{ position: 'absolute', bottom: theme.getSpacing('m') }}
      >
        <SwitchButton color="primary" variant="outline" size="small">
          <IconLabel
            Icon={<ArrowIcon component={ArrowLeftSvg} />}
            label={<FormattedMessage {...translations.switchHere} />}
            orientation="horizontal-reverse"
          />
        </SwitchButton>
      </motion.div>
    </Container>
  )
}

export function OrganizationCardPlaceholder() {
  return (
    <PlaceholderContainer>
      <Column>
        <PlaceholderCircle />
        <PlaceholderBar />
        <PlaceholderBar />
        <PlaceholderBar />
      </Column>
      <PlaceholderTag />
    </PlaceholderContainer>
  )
}

export default OrganizationCard
